import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabaseClient';
import SupportInbox from '../../components/SupportInbox';
import LanguageToggle from '../../components/LanguageToggle';
import { useLanguagePreference } from '../../i18n/useLanguagePreference';
import ImageCropModal from '../../components/ImageCropModal';
import ProfileCompletionBanner from '../../components/ProfileCompletionBanner';
import NotificationBell from '../../components/NotificationBell';
import StatCard from '../../components/StatCard';
import LegalLinksRow from '../../components/LegalLinksRow';
import CheckInPanel from '../../components/CheckInPanel';
import VocationWorksLogo from '../../components/VocationWorksLogo';
import { startConversationWithOrganization } from '../../utils/startConversationWithOrganization';
import { usePressFeedback } from '../../utils/usePressFeedback';

// Members-tab "Message" button. Off until the database supports organization conversations and
// Messaging handles an 'organization' side -- see utils/startConversationWithOrganization.js.
const ORG_MESSAGING_ENABLED = false;

const NAVY     = '#0D1B2A';
const NAVY_MID = '#132233';
const AMBER    = '#F5A623';
const WHITE    = '#FFFFFF';
const GREEN    = '#1D9E75';
const RED      = '#DC2626';
const BLUE     = '#3B82F6';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";
const NAV_H     = 56;
const SIDEBAR_W = 190;

const TABS = ['Dashboard', 'Members', 'Check-ins', 'Invites', 'Messages', 'Profile', 'Settings'];

const TAB_LABEL_KEYS = {
  'Dashboard': 'nav.organization.dashboard',
  'Members': 'nav.organization.members',
  'Check-ins': 'nav.organization.checkins',
  'Invites': 'nav.organization.invites',
  'Messages': 'nav.employer.messages',
  'Profile': 'nav.union.profile',
  'Settings': 'nav.organization.settings',
};

function IconGrid({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
    </svg>
  );
}
function IconUsers({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}
function IconClipboard({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
      <polyline points="9 14 11 16 15 12" />
    </svg>
  );
}
function IconMsg({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}
function IconCheck({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="8 12 11 15 16 9" />
    </svg>
  );
}
function IconMail({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <polyline points="2 7 12 13 22 7" />
    </svg>
  );
}
function IconBuilding({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M9 21V9" />
    </svg>
  );
}
function IconSettings({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function OrgAvatar({ logoUrl, initials, size = 36, bg = NAVY_MID, textColor = WHITE }) {
  if (logoUrl) {
    return (
      <div style={{ width: size, height: size, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, backgroundColor: bg }}>
        <img src={logoUrl} alt="Organization logo" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      </div>
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      backgroundColor: bg, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <span style={{ fontWeight: 700, fontSize: size * 0.33, color: textColor, fontFamily: FONT_BODY }}>
        {initials}
      </span>
    </div>
  );
}

// Extended beyond the task's 4 explicit tiers (Hired > Reviewing > Applied > Not Selected)
// to also rank the offer sub-states, since applications.status can carry those values too.
function statusRank(status) {
  if (status === 'hired')          return 6;
  if (status === 'offer_accepted') return 5;
  if (status === 'offer_sent')     return 4;
  if (status === 'reviewing')      return 3;
  if (status === 'offer_declined') return 1;
  if (status === 'rejected')       return 1;
  return 2; // interested / no status yet
}

function statusBadge(status, t) {
  if (status === 'hired')          return { label: t('organization.dashboard.members.statusHired'),         color: GREEN };
  if (status === 'offer_accepted') return { label: t('organization.dashboard.members.statusOfferAccepted'), color: GREEN };
  if (status === 'offer_sent')     return { label: t('organization.dashboard.members.statusOfferSent'),     color: '#8B5CF6' };
  if (status === 'reviewing')      return { label: t('organization.dashboard.members.statusReviewing'),     color: BLUE };
  if (status === 'offer_declined') return { label: t('organization.dashboard.members.statusOfferDeclined'), color: RED };
  if (status === 'rejected')       return { label: t('organization.dashboard.members.statusNotSelected'),   color: RED };
  return { label: t('organization.dashboard.members.statusApplied'), color: AMBER };
}

function typeLabel(type, t) {
  if (type === 'founding') return t('organization.dashboard.invites.typeFounding');
  if (type === 'staff')    return t('organization.dashboard.invites.typeStaff');
  if (type === 'member')   return t('organization.dashboard.invites.typeMember');
  return type;
}

function inviteStatusBadge(status, t) {
  if (status === 'accepted') return { label: t('organization.dashboard.invites.statusAccepted'), color: GREEN };
  if (status === 'expired')  return { label: t('organization.dashboard.invites.statusExpired'), color: 'rgba(255,255,255,0.4)' };
  if (status === 'revoked')  return { label: t('organization.dashboard.invites.statusRevoked'), color: RED };
  return { label: t('organization.dashboard.invites.statusPending'), color: AMBER };
}

const inp = {
  width: '100%', padding: '9px 12px', fontSize: '0.88rem',
  border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '8px',
  color: WHITE, outline: 'none', backgroundColor: 'rgba(255,255,255,0.06)',
  fontFamily: FONT_BODY,
};
const sel = { ...inp, cursor: 'pointer' };
const lbl = { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)', marginBottom: '5px' };
const fld = { marginBottom: '0.85rem' };
const g2  = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '0.85rem' };

// Module-level (not nested in OrganizationDashboard) so React keeps its identity across
// parent re-renders -- a component defined inside another component's render body gets
// a fresh function reference every render, which React treats as a brand-new component
// type and remounts, dropping focus out of its inputs on every keystroke.
function InviteForm({ inviteType, title, form, setForm, submitting, msg, msgIsSuccess, onSubmit }) {
  const { t } = useTranslation();
  return (
    <div style={{ backgroundColor: NAVY, borderRadius: '10px', padding: '18px 20px', flex: 1 }}>
      <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '12px' }}>
        {title}
      </p>
      <div style={fld}>
        <label style={lbl}>{t('organization.dashboard.invites.nameLabel')} <span style={{ fontWeight: 400, color: 'rgba(255,255,255,0.3)' }}>{t('organization.dashboard.invites.nameOptional')}</span></label>
        <input style={inp} placeholder={t('employer.signup.step1.fullNamePlaceholder')} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
      </div>
      <div style={fld}>
        <label style={lbl}>{t('organization.dashboard.invites.emailLabel')}</label>
        <input style={inp} type="email" placeholder="jane@example.com" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
      </div>
      <button
        onClick={() => onSubmit(inviteType)}
        disabled={submitting}
        style={{
          width: '100%', backgroundColor: AMBER, color: NAVY, border: 'none', borderRadius: '8px',
          padding: '10px', fontWeight: 700, fontSize: '0.85rem', cursor: submitting ? 'default' : 'pointer',
          opacity: submitting ? 0.7 : 1, fontFamily: FONT_BODY, marginTop: '4px',
        }}
      >
        {submitting ? t('organization.dashboard.invites.sendingButton') : inviteType === 'staff' ? t('organization.dashboard.invites.sendStaffInviteButton') : t('organization.dashboard.invites.sendMemberInviteButton')}
      </button>
      {msg && (
        <p style={{ fontSize: '0.78rem', fontWeight: 600, color: msgIsSuccess ? GREEN : RED, marginTop: '10px' }}>
          {msg}
        </p>
      )}
    </div>
  );
}

export default function OrganizationDashboard() {
  usePressFeedback();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const tabLabel = (tab) => TAB_LABEL_KEYS[tab] ? t(TAB_LABEL_KEYS[tab]) : tab;
  const [loading, setLoading] = useState(true);
  const [org, setOrg]         = useState(null);
  const [staffRow, setStaffRow] = useState(null);
  const [user, setUser]       = useState(null);
  useLanguagePreference(user?.id);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [activeTab, setActiveTab] = useState('Dashboard');

  const [funnelRows, setFunnelRows]     = useState([]);
  const [funnelLoaded, setFunnelLoaded] = useState(false);

  const [orgMembers, setOrgMembers]       = useState([]);
  const [workerNames, setWorkerNames]     = useState({});
  const [workerResumes, setWorkerResumes] = useState({});
  const [membersLoaded, setMembersLoaded] = useState(false);

  const [invites, setInvites]             = useState([]);
  const [invitesLoaded, setInvitesLoaded] = useState(false);

  const [staffForm, setStaffForm]             = useState({ name: '', email: '' });
  const [staffSubmitting, setStaffSubmitting] = useState(false);
  const [staffMsg, setStaffMsg]               = useState('');
  const [staffMsgIsSuccess, setStaffMsgIsSuccess] = useState(false);

  const [memberForm, setMemberForm]             = useState({ name: '', email: '' });
  const [memberSubmitting, setMemberSubmitting] = useState(false);
  const [memberMsg, setMemberMsg]               = useState('');
  const [memberMsgIsSuccess, setMemberMsgIsSuccess] = useState(false);

  const [profileForm, setProfileForm]     = useState(null);
  const [profileMsg, setProfileMsg]       = useState('');
  const [profileMsgIsError, setProfileMsgIsError] = useState(false);
  const logoInputRef                      = useRef(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoMsg, setLogoMsg]             = useState('');
  const [logoMsgIsError, setLogoMsgIsError] = useState(false);
  const [cropFile, setCropFile]           = useState(null);

  // ── Staff roster + admin-only staff actions (deactivate / reassign admin) ──
  const [staffList, setStaffList] = useState([]);
  const [staffLoaded, setStaffLoaded] = useState(false);
  const [staffActionSaving, setStaffActionSaving] = useState({});
  const [staffActionError, setStaffActionError] = useState('');
  const [reassignPick, setReassignPick] = useState('');
  const [reassignSaving, setReassignSaving] = useState(false);
  const [reassignStatus, setReassignStatus] = useState(null);
  // ── Admin-only member removal ──
  const [memberActionSaving, setMemberActionSaving] = useState({});
  const [memberActionError, setMemberActionError] = useState('');
  // ── This staff member's own display name + avatar (Settings > My Profile) ──
  const [myNameForm, setMyNameForm] = useState('');
  const [myProfileSaving, setMyProfileSaving] = useState(false);
  const [myProfileMsg, setMyProfileMsg] = useState('');
  const [myProfileMsgIsError, setMyProfileMsgIsError] = useState(false);
  const [avatarCropFile, setAvatarCropFile] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [pwForm, setPwForm] = useState({ newPw: '', confirmPw: '' });
  const [pwMsg, setPwMsg]   = useState('');
  const [pwMsgIsSuccess, setPwMsgIsSuccess] = useState(false);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel  = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
    const style = document.createElement('style');
    style.textContent = `
      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
      body { background: ${NAVY}; font-family: 'DM Sans', sans-serif; }
      input::placeholder, textarea::placeholder { color: rgba(255,255,255,0.28); }
      @media (max-width: 767px) {
        .org-sidebar   { display: none !important; }
        .org-main      { margin-left: 0 !important; padding-left: 16px !important; padding-right: 16px !important; padding-bottom: calc(68px + env(safe-area-inset-bottom)) !important; }
        .org-bottom-nav { display: flex !important; }
        .org-tab-bar    { display: none !important; }
        .org-nav-name   { display: none !important; }
        .org-stats-grid { grid-template-columns: 1fr 1fr !important; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(link);
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const uid = session?.user?.id;
      if (!uid) { navigate('/login'); return; }

      const { data: staff } = await supabase
        .from('organization_staff')
        .select('organization_id, role, display_name, avatar_url')
        .eq('user_id', uid)
        .maybeSingle();

      // This session isn't organization staff -- send them to whichever dashboard
      // actually matches their role instead of rendering this chrome around empty data,
      // matching the defense-in-depth guard already built for employer/union.
      if (!staff) {
        const { data: employerRecord } = await supabase.from('employers').select('id').eq('id', uid).single();
        if (employerRecord) { navigate('/employer/dashboard'); return; }
        const { data: unionRecord } = await supabase.from('union_accounts').select('id').eq('id', uid).single();
        if (unionRecord) { navigate('/union/dashboard'); return; }
        navigate('/login');
        return;
      }

      const { data: orgRow } = await supabase
        .from('organizations')
        .select('org_name, city, state, about, logo_url, website')
        .eq('id', staff.organization_id)
        .single();

      // Carry the name typed at signup (auth metadata full_name) into this person's own staff row
      // once, so Settings and the staff list show it instead of the email. Same self-update the
      // My profile form already does.
      const signupName = (session.user.user_metadata?.full_name || '').trim();
      if (!staff.display_name && signupName) {
        const { error: nameErr } = await supabase.from('organization_staff')
          .update({ display_name: signupName }).eq('organization_id', staff.organization_id).eq('user_id', uid);
        if (!nameErr) staff.display_name = signupName;
      }
      setUser(session.user);
      setStaffRow(staff);
      setOrg(orgRow);
      setProfileForm({
        orgName: orgRow?.org_name || '',
        about:   orgRow?.about    || '',
        city:    orgRow?.city     || '',
        state:   orgRow?.state    || '',
        website: orgRow?.website  || '',
      });
      setLoading(false);
    };
    init();
  }, [navigate]);

  // ── Stat cards: computed purely from the funnel RPC, per spec ──
  useEffect(() => {
    if (!staffRow?.organization_id) return;
    const loadFunnel = async () => {
      const { data } = await supabase.rpc('get_organization_member_funnel', { p_organization_id: staffRow.organization_id });
      setFunnelRows(data || []);
      setFunnelLoaded(true);
    };
    loadFunnel();
  }, [staffRow?.organization_id]);

  // ── Members tab: organization_members is the source of truth for "who's a member"
  // (funnel data alone would miss members with zero applications), enriched with
  // names from workers (for members the funnel doesn't cover) and funnel status. ──
  useEffect(() => {
    if (!staffRow?.organization_id) return;
    const loadMembers = async () => {
      const { data: members } = await supabase
        .from('organization_members')
        .select('id, worker_id, joined_at, left_at')
        .eq('organization_id', staffRow.organization_id)
        .order('joined_at', { ascending: false });
      setOrgMembers(members || []);

      const workerIds = [...new Set((members || []).map(m => m.worker_id))];
      if (workerIds.length > 0) {
        const { data: workers } = await supabase.from('workers').select('id, first_name, last_name, resume_url').in('id', workerIds);
        setWorkerNames(Object.fromEntries((workers || []).map(w => [w.id, `${w.first_name || ''} ${w.last_name || ''}`.trim()])));
        setWorkerResumes(Object.fromEntries((workers || []).map(w => [w.id, w.resume_url || null])));
      }
      setMembersLoaded(true);
    };
    loadMembers();
  }, [staffRow?.organization_id]);

  useEffect(() => {
    if (!staffRow?.organization_id) return;
    const loadInvites = async () => {
      const { data } = await supabase
        .from('organization_invites')
        .select('*')
        .eq('organization_id', staffRow.organization_id)
        .order('created_at', { ascending: false });
      setInvites(data || []);
      setInvitesLoaded(true);
    };
    loadInvites();
  }, [staffRow?.organization_id]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const handleCreateInvite = async (inviteType) => {
    const form        = inviteType === 'staff' ? staffForm : memberForm;
    const setForm      = inviteType === 'staff' ? setStaffForm : setMemberForm;
    const setSubmitting = inviteType === 'staff' ? setStaffSubmitting : setMemberSubmitting;
    const setMsg        = inviteType === 'staff' ? setStaffMsg : setMemberMsg;
    const setMsgIsSuccess = inviteType === 'staff' ? setStaffMsgIsSuccess : setMemberMsgIsSuccess;

    if (!form.email.trim()) { setMsg(t('organization.dashboard.invites.emailRequiredError')); setMsgIsSuccess(false); return; }
    setMsg('');
    setSubmitting(true);

    const code = `${inviteType}-${crypto.randomUUID()}`;
    const { data: newInvite, error: insertError } = await supabase
      .from('organization_invites')
      .insert({
        organization_id: staffRow.organization_id,
        invited_by: user.id,
        invite_type: inviteType,
        invite_code: code,
        email: form.email.trim(),
        status: 'pending',
      })
      .select('*')
      .single();

    if (insertError) {
      setMsg(insertError.message);
      setMsgIsSuccess(false);
      setSubmitting(false);
      return;
    }

    // Best-effort, matching the rest of the app's notification pattern (sendNotificationEmail) --
    // a failed send shouldn't undo an otherwise-successful invite; the DB row is the source of truth.
    try {
      await fetch('/api/notify-interest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'org_invite',
          recipient_email: form.email.trim(),
          recipient_name: form.name.trim(),
          org_name: org?.org_name || 'your organization',
          invite_type: inviteType,
          invite_link: `${window.location.origin}/organization/join?code=${code}`,
        }),
      });
    } catch (_) {}

    setInvites(prev => [newInvite, ...prev]);
    setForm({ name: '', email: '' });
    setSubmitting(false);
    setMsg(t('organization.dashboard.invites.inviteSentMsg'));
    setMsgIsSuccess(true);
  };

  // ── Pending member invites: copy the join link, resend the email, or revoke ──
  const inviteLink = (inv) => `${window.location.origin}/organization/join?code=${encodeURIComponent(inv.invite_code)}`;
  // { [inviteId]: 'copied' | 'resent' | 'error:<message>' } -- short-lived per-row feedback.
  const [inviteAction, setInviteAction] = useState({});
  const [inviteBusyId, setInviteBusyId] = useState(null);
  const [confirmRevokeId, setConfirmRevokeId] = useState(null);
  const flashInvite = (id, value) => {
    setInviteAction(prev => ({ ...prev, [id]: value }));
    setTimeout(() => setInviteAction(prev => (prev[id] === value ? { ...prev, [id]: undefined } : prev)), 4000);
  };
  const handleCopyInviteLink = async (inv) => {
    try {
      await navigator.clipboard.writeText(inviteLink(inv));
      flashInvite(inv.id, 'copied');
    } catch (_) {
      window.prompt(t('organization.dashboard.invites.copyPrompt'), inviteLink(inv));
    }
  };
  const handleResendInvite = async (inv) => {
    setInviteBusyId(inv.id);
    try {
      const res = await fetch('/api/notify-interest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'org_invite',
          recipient_email: inv.email,
          recipient_name: '',
          org_name: org?.org_name || 'your organization',
          invite_type: inv.invite_type,
          invite_link: inviteLink(inv),
        }),
      });
      flashInvite(inv.id, res.ok ? 'resent' : `error:${t('organization.dashboard.invites.resendError')}`);
    } catch (_) {
      flashInvite(inv.id, `error:${t('organization.dashboard.invites.resendError')}`);
    }
    setInviteBusyId(null);
  };
  // Revoke = status 'revoked' (the row stays for the record). .select() confirms a row actually
  // changed, so an update that RLS silently filters out is reported instead of looking done.
  const handleRevokeInvite = async (inv) => {
    setInviteBusyId(inv.id);
    const { data, error } = await supabase
      .from('organization_invites')
      .update({ status: 'revoked' })
      .eq('id', inv.id)
      .eq('status', 'pending')
      .select('id, status');
    setInviteBusyId(null);
    setConfirmRevokeId(null);
    if (error || !data || data.length !== 1) {
      flashInvite(inv.id, `error:${error?.message || t('organization.dashboard.invites.revokeError')}`);
      return;
    }
    setInvites(prev => prev.map(i => (i.id === inv.id ? { ...i, status: 'revoked' } : i)));
  };

  // Opens (or creates) the conversation with a member, then goes to Messages. Only reachable
  // once ORG_MESSAGING_ENABLED is on.
  // Kept for the Messages tab to open once it can show organization conversations.
  const orgConversationRef = useRef(null);
  const [messagingMemberId, setMessagingMemberId] = useState(null);
  const handleMessageMember = async (member) => {
    setMessagingMemberId(member.id);
    setMemberActionError('');
    const convId = await startConversationWithOrganization(supabase, member.worker_id, staffRow.organization_id);
    setMessagingMemberId(null);
    if (!convId) { setMemberActionError(t('organization.dashboard.members.messageError')); return; }
    orgConversationRef.current = convId;
    setActiveTab('Messages');
  };

  const setPf = (k, v) => setProfileForm(p => ({ ...p, [k]: v }));

  const handleSaveProfile = async () => {
    if (!profileForm || !staffRow?.organization_id) return;
    const { error } = await supabase.from('organizations').update({
      org_name: profileForm.orgName,
      about:    profileForm.about   || null,
      city:     profileForm.city    || null,
      state:    profileForm.state   || null,
      website:  profileForm.website || null,
    }).eq('id', staffRow.organization_id);
    if (error) {
      setProfileMsg(t('organization.dashboard.profile.errorSavingMsg'));
      setProfileMsgIsError(true);
    } else {
      setOrg(prev => ({
        ...prev,
        org_name: profileForm.orgName,
        about:    profileForm.about   || null,
        city:     profileForm.city    || null,
        state:    profileForm.state   || null,
        website:  profileForm.website || null,
      }));
      setProfileMsg(t('organization.dashboard.profile.changesSavedMsg'));
      setProfileMsgIsError(false);
      setTimeout(() => setProfileMsg(''), 3000);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !staffRow?.organization_id) return;
    setCropFile(file);
  };

  const handleLogoCropConfirm = async (blob, outputType) => {
    setCropFile(null);
    setLogoUploading(true);
    setLogoMsg('');
    const path = `${staffRow.organization_id}/logo`;
    const { error: uploadError } = await supabase.storage
      .from('organization-logos')
      .upload(path, blob, { upsert: true, contentType: outputType });
    if (uploadError) {
      setLogoMsg(t('organization.dashboard.profile.uploadFailedMsg'));
      setLogoMsgIsError(true);
      setLogoUploading(false);
      return;
    }
    const { data: urlData } = supabase.storage.from('organization-logos').getPublicUrl(path);
    const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`;
    const { error: updateError } = await supabase.from('organizations').update({ logo_url: publicUrl }).eq('id', staffRow.organization_id);
    if (updateError) {
      setLogoMsg(t('organization.dashboard.profile.errorSavingMsg'));
      setLogoMsgIsError(true);
      setLogoUploading(false);
      return;
    }
    setOrg(prev => ({ ...prev, logo_url: publicUrl }));
    setLogoMsg(t('organization.dashboard.profile.logoSavedMsg'));
    setLogoMsgIsError(false);
    setLogoUploading(false);
  };

  // ── Staff roster ───────────────────────────────────────────────────────────
  // organization_staff has display_name now, but a seat that never set one still needs an
  // identity, so the accepted invite's email is the fallback (same source the platform Admin
  // dashboard uses). Deactivated seats stay in this list -- deactivation is reversible.
  const loadStaff = async (organizationId) => {
    const [{ data: staff }, { data: acceptedInvites }] = await Promise.all([
      supabase.from('organization_staff')
        .select('user_id, role, display_name, avatar_url, is_active, created_at')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: true }),
      supabase.from('organization_invites')
        .select('accepted_by, email')
        .eq('organization_id', organizationId)
        .not('accepted_by', 'is', null),
    ]);
    const emailByUser = Object.fromEntries((acceptedInvites || []).map(i => [i.accepted_by, i.email]));
    setStaffList((staff || []).map(r => ({ ...r, email: emailByUser[r.user_id] || null })));
    setStaffLoaded(true);
  };

  useEffect(() => {
    if (!staffRow?.organization_id) return;
    loadStaff(staffRow.organization_id);
  }, [staffRow?.organization_id]); // eslint-disable-line

  const staffLabel = (m) => m.display_name
    || (m.user_id === user?.id ? user?.user_metadata?.full_name : '')
    || m.email || m.user_id.slice(0, 8);

  useEffect(() => { setMyNameForm(staffRow?.display_name || user?.user_metadata?.full_name || ''); }, [staffRow?.display_name, user?.id]); // eslint-disable-line

  // Single source of truth for every admin-only control on this page. Kept derived (not state)
  // so a live admin handoff flips the whole UI at once.
  const isOrgAdmin = staffRow?.role === 'admin';

  // Soft deactivation, mirroring the employer Team tab: flips is_active, never deletes, so the
  // person's history and attribution survive. guard_org_staff_deactivation enforces admin-only
  // and no-self-target in the DB regardless of what this does; surface its message if it fires.
  const handleToggleStaffActive = async (member) => {
    if (!staffRow?.organization_id) return;
    const deactivating = member.is_active !== false;
    const confirmed = window.confirm(t(
      deactivating ? 'organization.dashboard.staff.deactivateConfirm' : 'organization.dashboard.staff.reactivateConfirm',
      { name: staffLabel(member) },
    ));
    if (!confirmed) return;
    setStaffActionSaving(prev => ({ ...prev, [member.user_id]: true }));
    setStaffActionError('');
    const { error } = await supabase
      .from('organization_staff')
      .update({ is_active: !deactivating })
      .eq('organization_id', staffRow.organization_id)
      .eq('user_id', member.user_id);
    setStaffActionSaving(prev => ({ ...prev, [member.user_id]: false }));
    if (error) { setStaffActionError(error.message); return; }
    setStaffList(prev => prev.map(m => m.user_id === member.user_id ? { ...m, is_active: !deactivating } : m));
  };

  // Hand the admin role to another active staff member. The same RPC the platform Admin
  // dashboard calls -- it now accepts the org's own admin too, and swaps both rows in one
  // transaction, so re-read from the server rather than guessing the end state locally.
  const handleReassignAdmin = async () => {
    if (!reassignPick || !staffRow?.organization_id) return;
    const target = staffList.find(m => m.user_id === reassignPick);
    const current = staffList.find(m => m.role === 'admin');
    const confirmed = window.confirm(t('organization.dashboard.staff.reassignConfirm', {
      from: current ? staffLabel(current) : '—',
      to: target ? staffLabel(target) : '—',
    }));
    if (!confirmed) return;
    setReassignSaving(true);
    setReassignStatus(null);
    const { error } = await supabase.rpc('reassign_organization_admin', {
      p_organization_id: staffRow.organization_id,
      p_new_admin_user_id: reassignPick,
    });
    setReassignSaving(false);
    if (error) { setReassignStatus({ ok: false, msg: error.message }); return; }
    setReassignStatus({ ok: true, msg: t('organization.dashboard.staff.reassignSuccess') });
    setReassignPick('');
    await loadStaff(staffRow.organization_id);
    // The handoff demotes this session too, so every admin-only control on this page has to
    // re-evaluate immediately rather than waiting for a reload.
    setStaffRow(prev => (prev ? { ...prev, role: 'staff' } : prev));
  };

  // ── Member removal (admin) ────────────────────────────────────────────────
  // Sets left_at rather than deleting, so check-ins, messages and funnel history stay
  // attributable. guard_org_member_leave blocks un-leaving, so this is deliberately one-way --
  // the confirm says so, and re-joining means issuing a fresh invite.
  const handleRemoveMember = async (member) => {
    if (!staffRow?.organization_id) return;
    if (!window.confirm(t('organization.dashboard.members.removeConfirm', { name: member.name }))) return;
    setMemberActionSaving(prev => ({ ...prev, [member.id]: true }));
    setMemberActionError('');
    const { error } = await supabase
      .from('organization_members')
      .update({ left_at: new Date().toISOString() })
      .eq('id', member.id);
    setMemberActionSaving(prev => ({ ...prev, [member.id]: false }));
    if (error) { setMemberActionError(error.message); return; }
    setOrgMembers(prev => prev.map(m => m.id === member.id ? { ...m, left_at: new Date().toISOString() } : m));
  };

  // ── My Profile: this staff member's own name + avatar ─────────────────────
  // Reuses the shared staff-avatars bucket (its storage policies key on the uploader's own
  // auth.uid() folder, not on employer vs organization) and the same ImageCropModal the org
  // logo upload already uses, rather than introducing a second upload path.
  const handleSaveMyName = async () => {
    if (!user?.id || !staffRow?.organization_id) return;
    setMyProfileSaving(true);
    setMyProfileMsg('');
    const trimmed = myNameForm.trim();
    const { error } = await supabase
      .from('organization_staff')
      .update({ display_name: trimmed || null })
      .eq('organization_id', staffRow.organization_id)
      .eq('user_id', user.id);
    setMyProfileSaving(false);
    if (error) { setMyProfileMsg(error.message); setMyProfileMsgIsError(true); return; }
    setStaffRow(prev => (prev ? { ...prev, display_name: trimmed || null } : prev));
    setStaffList(prev => prev.map(m => m.user_id === user.id ? { ...m, display_name: trimmed || null } : m));
    setMyProfileMsg(t('organization.dashboard.myProfile.savedMsg'));
    setMyProfileMsgIsError(false);
    setTimeout(() => setMyProfileMsg(''), 3000);
  };

  const handleAvatarPick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setAvatarCropFile(file);
  };

  const handleAvatarCropConfirm = async (blob, outputType) => {
    setAvatarCropFile(null);
    if (!user?.id || !staffRow?.organization_id) return;
    setAvatarUploading(true);
    setMyProfileMsg('');
    const path = `${user.id}/avatar`;
    const { error: uploadError } = await supabase.storage
      .from('staff-avatars')
      .upload(path, blob, { upsert: true, contentType: outputType });
    if (uploadError) {
      setMyProfileMsg(t('organization.dashboard.myProfile.avatarUploadFailed'));
      setMyProfileMsgIsError(true);
      setAvatarUploading(false);
      return;
    }
    const { data: urlData } = supabase.storage.from('staff-avatars').getPublicUrl(path);
    const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`;
    const { error: updateError } = await supabase
      .from('organization_staff')
      .update({ avatar_url: publicUrl })
      .eq('organization_id', staffRow.organization_id)
      .eq('user_id', user.id);
    setAvatarUploading(false);
    if (updateError) { setMyProfileMsg(updateError.message); setMyProfileMsgIsError(true); return; }
    setStaffRow(prev => (prev ? { ...prev, avatar_url: publicUrl } : prev));
    setStaffList(prev => prev.map(m => m.user_id === user.id ? { ...m, avatar_url: publicUrl } : m));
    setMyProfileMsg(t('organization.dashboard.myProfile.avatarSavedMsg'));
    setMyProfileMsgIsError(false);
    setTimeout(() => setMyProfileMsg(''), 3000);
  };

  const handleChangePassword = async () => {
    if (pwForm.newPw !== pwForm.confirmPw) { setPwMsg(t('worker.dashboard.settings.passwordMismatch')); setPwMsgIsSuccess(false); return; }
    if (pwForm.newPw.length < 8) { setPwMsg(t('worker.dashboard.settings.passwordTooShort')); setPwMsgIsSuccess(false); return; }
    const { error } = await supabase.auth.updateUser({ password: pwForm.newPw });
    if (error) {
      setPwMsg(error.message);
      setPwMsgIsSuccess(false);
    } else {
      setPwMsg(t('employer.dashboard.settings.passwordUpdated'));
      setPwMsgIsSuccess(true);
      setPwForm({ newPw: '', confirmPw: '' });
      setTimeout(() => setPwMsg(''), 3000);
    }
  };

  if (loading) {
    return <div style={{ background: NAVY, height: '100dvh' }}></div>;
  }

  // ── Derived: stat cards (funnel RPC only, per spec) ──
  const totalMembers     = new Set(funnelRows.map(r => r.worker_id)).size;
  const appliedCount     = funnelRows.filter(r => !r.status || r.status === 'interested').length;
  const interviewingCount = funnelRows.filter(r => r.status === 'reviewing').length;
  const hiredCount       = funnelRows.filter(r => r.status === 'hired').length;

  // ── Derived: members tab list (organization_members joined with funnel data) ──
  const funnelByWorker = {};
  funnelRows.forEach(r => {
    (funnelByWorker[r.worker_id] = funnelByWorker[r.worker_id] || []).push(r);
  });
  const membersList = orgMembers.map(m => {
    const apps = funnelByWorker[m.worker_id] || [];
    const best = apps.reduce((acc, a) => (!acc || statusRank(a.status) > statusRank(acc.status)) ? a : acc, null);
    const nameFromFunnel = apps[0] ? `${apps[0].worker_first_name || ''} ${apps[0].worker_last_name || ''}`.trim() : '';
    return {
      id: m.id,
      worker_id: m.worker_id,
      joined_at: m.joined_at,
      left_at: m.left_at,
      name: nameFromFunnel || workerNames[m.worker_id] || t('organization.dashboard.members.fallbackName'),
      resume_url: workerResumes[m.worker_id] || null,
      status: best?.status || null,
      job_title: best?.job_title || null,
    };
  });

  const orgInitials = (org?.org_name || '')
    .split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';

  // ── Profile completion checklist (closes the Phase 5 gap now that Profile exists to link to) ──
  const profileCompletionItems = [
    { done: !!org?.logo_url, label: t('organization.dashboard.profile.orgLogoLabel'),   tab: 'Profile' },
    { done: !!org?.about,    label: t('organization.dashboard.profile.aboutDescriptionLabel'),   tab: 'Profile' },
    { done: !!org?.website,  label: t('organization.dashboard.profile.websiteLabel'),              tab: 'Profile' },
  ];
  const profileCompletedCount = profileCompletionItems.filter(i => i.done).length;
  const profileCompletePct    = Math.round((profileCompletedCount / profileCompletionItems.length) * 100 / 25) * 25;
  const profileMissingItems   = profileCompletionItems.filter(i => !i.done);

  return (
    <div style={{ minHeight: '100dvh', backgroundColor: NAVY, fontFamily: FONT_BODY }}>

      {/* ── Top Navbar ── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, height: NAV_H,
        backgroundColor: NAVY, borderBottom: `2px solid ${AMBER}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 24px', zIndex: 100,
      }}>
        <div onClick={() => setActiveTab('Dashboard')} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
          <VocationWorksLogo variant="reversed" size={isMobile ? 28 : 36} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          {/* Hidden below 768px like the employer header's company name: at phone width it pushed
              the language toggle, bell and avatar past the right edge, leaving the bell untappable. */}
          <span className="org-nav-name" style={{ fontSize: '0.875rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)' }}>
            {org?.org_name}
          </span>
          <LanguageToggle userId={user?.id} />
          <NotificationBell userId={user?.id} onNavigate={(tab) => setActiveTab(tab)} />
          <OrgAvatar logoUrl={org?.logo_url} initials={orgInitials} size={36} bg={NAVY_MID} textColor={WHITE} />
        </div>
      </nav>

      {/* ── Left Sidebar ── */}
      <aside className="org-sidebar" style={{
        position: 'fixed', top: NAV_H, left: 0, bottom: 0, width: SIDEBAR_W,
        backgroundColor: NAVY_MID, zIndex: 99,
        display: 'flex', flexDirection: 'column', overflowY: 'auto',
      }}>
        <div style={{ padding: '20px 14px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)', textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <OrgAvatar logoUrl={org?.logo_url} initials={orgInitials} size={60} bg={NAVY} textColor={WHITE} />
          </div>
          <p style={{ marginTop: '10px', fontWeight: 700, fontSize: '0.88rem', color: WHITE, lineHeight: 1.3 }}>
            {org?.org_name}
          </p>
          {(org?.city || org?.state) && (
            <p style={{ fontSize: '0.77rem', color: 'rgba(255,255,255,0.42)', marginTop: '4px' }}>
              {[org?.city, org?.state].filter(Boolean).join(', ')}
            </p>
          )}
          <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'center' }}>
            <span style={{
              backgroundColor: 'rgba(29,158,117,0.15)', border: `1px solid ${GREEN}`,
              borderRadius: '999px', padding: '3px 12px',
              fontSize: '0.7rem', fontWeight: 700, color: GREEN,
            }}>
              {t('organization.dashboard.shared.activeStatus')}
            </span>
          </div>
          <div style={{ marginTop: '14px' }}>
            <p style={{ fontSize: '0.7rem', fontWeight: 600, color: profileCompletePct === 100 ? AMBER : 'rgba(255,255,255,0.4)', marginBottom: '6px' }}>
              {profileCompletePct === 100 ? t('organization.dashboard.shared.profileComplete') : t('organization.dashboard.shared.profilePctComplete', { pct: profileCompletePct })}
            </p>
            <div style={{ height: '4px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '2px', overflow: 'hidden' }}>
              <div style={{ width: `${profileCompletePct}%`, height: '100%', backgroundColor: AMBER, borderRadius: '2px' }} />
            </div>
            {profileMissingItems.length > 0 && (
              <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                {profileMissingItems.map(item => (
                  <button
                    key={item.label}
                    onClick={() => setActiveTab(item.tab)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '5px', fontFamily: FONT_BODY }}
                  >
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: AMBER, lineHeight: 1 }}>+</span>
                    <span style={{ fontSize: '0.72rem', color: AMBER, fontWeight: 500 }}>{item.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{ padding: '14px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px' }}>
            {t('organization.dashboard.shared.signedInAs')}
          </p>
          <p style={{ fontSize: '0.85rem', fontWeight: 700, color: WHITE, marginBottom: '2px', overflowWrap: 'break-word' }}>
            {staffRow?.display_name || user?.user_metadata?.full_name || t('organization.dashboard.shared.staffMember')}
          </p>
          <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '4px', overflowWrap: 'break-word' }}>
            {user?.email}
          </p>
          <p style={{ fontSize: '0.72rem', color: AMBER, fontWeight: 600, textTransform: 'capitalize' }}>
            {staffRow?.role === 'admin' ? t('organization.dashboard.shared.roleAdmin') : t('organization.dashboard.shared.roleStaff')}
          </p>
        </div>

        <div style={{ marginTop: 'auto', padding: '14px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <button
            onClick={handleSignOut}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '8px 10px', borderRadius: '8px', width: '100%',
              color: '#F87171', fontFamily: FONT_BODY, fontSize: '0.82rem', fontWeight: 600,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            {t('common.signOut')}
          </button>
        </div>

        {/* Legal links — pinned below Sign out */}
        <div style={{ padding: '0 14px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <LegalLinksRow fontSize="0.68rem" wrap />
          <p style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.25)', fontFamily: FONT_BODY }}>{t('common.copyright')}</p>
        </div>
      </aside>

      {/* ── Main Content ── */}
      <main className="org-main" style={{
        marginLeft: SIDEBAR_W, marginTop: NAV_H,
        minHeight: `calc(100dvh - ${NAV_H}px)`,
        backgroundColor: NAVY,
        display: 'flex', flexDirection: 'column',
      }}>

        {/* Tab bar */}
        <div className="org-tab-bar" style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0, overflowX: 'auto' }}>
          {TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '12px 20px', fontSize: '0.86rem', fontWeight: 600,
                fontFamily: FONT_BODY, whiteSpace: 'nowrap', flexShrink: 0,
                color: activeTab === tab ? AMBER : 'rgba(255,255,255,0.4)',
                borderBottom: activeTab === tab ? `2px solid ${AMBER}` : '2px solid transparent',
                marginBottom: '-1px',
              }}
            >
              {tabLabel(tab)}
            </button>
          ))}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', paddingBottom: '40px' }}>

          {/* ── Dashboard tab ── */}
          {activeTab === 'Dashboard' && (
            <div>
              <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '16px' }}>
                {t('organization.dashboard.shared.dashboardHeading')}
              </p>

              {staffRow?.organization_id && (
                <ProfileCompletionBanner
                  storageKey={`profileBannerDismissed_organization_${staffRow.organization_id}`}
                  profileLabel={t('organization.dashboard.shared.orgProfileLabel')}
                  missingItems={profileMissingItems}
                  pct={profileCompletePct}
                  onNavigate={setActiveTab}
                />
              )}

              <div className="org-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '28px' }}>
                <StatCard label={t('organization.dashboard.home.statTotalMembers')} value={funnelLoaded ? totalMembers : '—'} Icon={IconUsers} iconColor={AMBER} />
                <StatCard label={t('organization.dashboard.home.statApplied')} value={funnelLoaded ? appliedCount : '—'} Icon={IconClipboard} iconColor={AMBER} />
                <StatCard label={t('organization.dashboard.home.statInterviewing')} value={funnelLoaded ? interviewingCount : '—'} Icon={IconMsg} iconColor={BLUE} />
                <StatCard label={t('organization.dashboard.home.statHired')} value={funnelLoaded ? hiredCount : '—'} Icon={IconCheck} iconColor={GREEN} />
              </div>

              {org?.about && (
                <div style={{ backgroundColor: NAVY_MID, borderRadius: '14px', padding: '24px', maxWidth: '640px' }}>
                  <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px' }}>
                    {t('organization.dashboard.home.aboutHeading')}
                  </p>
                  <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>
                    {org.about}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ── Members tab ── */}
          {activeTab === 'Members' && (
            <div>
              {/* ── Staff roster ──────────────────────────────────────────────
                  Lives above the member list rather than in its own tab: the mobile bottom nav
                  already carries seven items, and both lists answer "who is in this org". */}
              <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px' }}>
                {t('organization.dashboard.staff.heading')}
              </p>
              {!staffLoaded ? (
                <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.4)', marginBottom: '24px' }}>{t('organization.dashboard.shared.loading')}</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                  {staffList.map(m => {
                    const inactive = m.is_active === false;
                    // Admin-only, and never the admin's own row -- the DB trigger blocks
                    // self-targeting anyway, so hiding it just avoids an action that must fail.
                    const canToggle = isOrgAdmin && m.user_id !== user?.id;
                    return (
                      <div
                        key={m.user_id}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
                          backgroundColor: NAVY_MID, borderRadius: '8px', padding: '10px 14px',
                          opacity: inactive ? 0.55 : 1,
                        }}
                      >
                        <OrgAvatar logoUrl={m.avatar_url} initials={(staffLabel(m) || '?')[0].toUpperCase()} size={36} bg={NAVY} textColor={WHITE} />
                        <div style={{ flex: 1, minWidth: '110px' }}>
                          <div style={{
                            fontSize: '0.85rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            color: inactive ? 'rgba(255,255,255,0.55)' : WHITE,
                            textDecoration: inactive ? 'line-through' : 'none',
                          }}>
                            {staffLabel(m)}
                          </div>
                        </div>
                        {inactive && (
                          <span style={{ fontSize: '0.68rem', fontWeight: 700, color: RED, backgroundColor: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: '999px', padding: '2px 10px', flexShrink: 0 }}>
                            {t('organization.dashboard.staff.inactiveBadge')}
                          </span>
                        )}
                        <span style={{
                          fontSize: '0.68rem', fontWeight: 700, borderRadius: '999px', padding: '2px 10px', flexShrink: 0, textTransform: 'capitalize',
                          backgroundColor: m.role === 'admin' ? 'rgba(245,166,35,0.15)' : 'rgba(255,255,255,0.06)',
                          color: m.role === 'admin' ? AMBER : 'rgba(255,255,255,0.5)',
                        }}>
                          {m.role}
                        </span>
                        {canToggle && (
                          <button
                            onClick={() => handleToggleStaffActive(m)}
                            disabled={!!staffActionSaving[m.user_id]}
                            style={{
                              background: 'transparent', borderRadius: '6px', fontSize: '0.72rem', fontWeight: 700,
                              padding: '5px 10px', flexShrink: 0, fontFamily: FONT_BODY,
                              border: `1px solid ${inactive ? 'rgba(29,158,117,0.45)' : 'rgba(255,255,255,0.2)'}`,
                              color: inactive ? GREEN : 'rgba(255,255,255,0.6)',
                              cursor: staffActionSaving[m.user_id] ? 'default' : 'pointer',
                              opacity: staffActionSaving[m.user_id] ? 0.5 : 1,
                            }}
                          >
                            {t(inactive ? 'organization.dashboard.staff.reactivateButton' : 'organization.dashboard.staff.deactivateButton')}
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {staffActionError && (
                    <p style={{ fontSize: '0.8rem', color: RED, lineHeight: 1.5, backgroundColor: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.25)', borderRadius: '8px', padding: '10px 12px', margin: 0 }}>
                      {staffActionError}
                    </p>
                  )}
                </div>
              )}

              {/* Admin handoff -- only the current admin sees it, and only active staff can
                  receive it (the RPC rejects an inactive target outright). */}
              {isOrgAdmin && staffLoaded && (() => {
                const targets = staffList.filter(m => m.role !== 'admin' && m.is_active !== false);
                return (
                  <div style={{ background: NAVY_MID, borderRadius: '8px', padding: '14px 16px', marginBottom: '28px' }}>
                    <p style={{ fontSize: '0.78rem', fontWeight: 700, color: WHITE, marginBottom: '4px' }}>
                      {t('organization.dashboard.staff.reassignHeading')}
                    </p>
                    <p style={{ fontSize: '0.76rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1.5, marginBottom: '12px' }}>
                      {t('organization.dashboard.staff.reassignDescription')}
                    </p>
                    {targets.length === 0 ? (
                      <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.4)', margin: 0 }}>
                        {t('organization.dashboard.staff.reassignNoTargets')}
                      </p>
                    ) : (
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <select
                          value={reassignPick}
                          onChange={e => setReassignPick(e.target.value)}
                          style={{ ...sel, width: 'auto', minWidth: '180px', maxWidth: '100%' }}
                        >
                          <option value="">{t('organization.dashboard.staff.reassignSelectPlaceholder')}</option>
                          {targets.map(m => <option key={m.user_id} value={m.user_id}>{staffLabel(m)}</option>)}
                        </select>
                        <button
                          onClick={handleReassignAdmin}
                          disabled={!reassignPick || reassignSaving}
                          style={{
                            background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.82rem',
                            padding: '9px 18px', borderRadius: '8px', fontFamily: FONT_BODY,
                            cursor: (!reassignPick || reassignSaving) ? 'default' : 'pointer',
                            opacity: (!reassignPick || reassignSaving) ? 0.5 : 1,
                          }}
                        >
                          {reassignSaving ? t('organization.dashboard.shared.loading') : t('organization.dashboard.staff.reassignButton')}
                        </button>
                        {reassignStatus && (
                          <span style={{ fontSize: '0.8rem', fontWeight: 600, color: reassignStatus.ok ? GREEN : RED }}>{reassignStatus.msg}</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '16px' }}>
                {t('organization.dashboard.members.heading')}
              </p>

              {!membersLoaded ? (
                <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.4)' }}>{t('organization.dashboard.shared.loading')}</p>
              ) : membersList.length === 0 ? (
                <div style={{ background: NAVY_MID, borderRadius: '8px', padding: '20px', textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>
                  {t('organization.dashboard.members.empty')}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {membersList.map(m => {
                    const badge = m.status ? statusBadge(m.status, t) : null;
                    // Departed members stay listed (greyed, struck through, "Departed" badge)
                    // to match the deactivated-staff treatment above -- their check-in and
                    // message history is still attributable, so hiding them would lose context.
                    const departed = !!m.left_at;
                    return (
                      <div
                        key={m.id}
                        style={{
                          display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center',
                          justifyContent: 'space-between', gap: isMobile ? '6px' : '10px',
                          backgroundColor: NAVY_MID, borderRadius: '8px', padding: '14px 16px',
                          opacity: departed ? 0.55 : 1,
                        }}
                      >
                        <div>
                          <p style={{
                            fontSize: '0.88rem', fontWeight: 600, marginBottom: '2px',
                            color: departed ? 'rgba(255,255,255,0.55)' : WHITE,
                            textDecoration: departed ? 'line-through' : 'none',
                          }}>{m.name}</p>
                          {m.job_title && (
                            <p style={{ fontSize: '0.76rem', color: 'rgba(255,255,255,0.4)' }}>{m.job_title}</p>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {badge ? (
                            <span style={{ fontSize: '0.7rem', fontWeight: 700, color: badge.color, backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: '999px', padding: '3px 12px' }}>
                              {badge.label}
                            </span>
                          ) : (
                            <span style={{ fontSize: '0.7rem', fontWeight: 600, color: 'rgba(255,255,255,0.35)' }}>{t('organization.dashboard.members.noApplicationsYet')}</span>
                          )}
                          {m.resume_url ? (
                            <button
                              onClick={() => window.open(m.resume_url, '_blank', 'noopener,noreferrer')}
                              style={{ background: 'rgba(245,166,35,0.1)', border: '1px solid rgba(245,166,35,0.35)', color: AMBER, fontSize: '0.72rem', fontWeight: 700, padding: '5px 12px', borderRadius: '999px', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}
                            >
                              {t('organization.dashboard.members.viewResume')}
                            </button>
                          ) : (
                            <span style={{ fontSize: '0.68rem', fontWeight: 600, color: 'rgba(255,255,255,0.25)', whiteSpace: 'nowrap' }}>{t('organization.dashboard.members.noResume')}</span>
                          )}
                          {departed && (
                            <span style={{ fontSize: '0.68rem', fontWeight: 700, color: RED, backgroundColor: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: '999px', padding: '2px 10px', whiteSpace: 'nowrap' }}>
                              {t('organization.dashboard.members.departedBadge')}
                            </span>
                          )}
                          {ORG_MESSAGING_ENABLED && !departed && (
                            <button
                              type="button"
                              data-member-message
                              onClick={() => handleMessageMember(m)}
                              disabled={messagingMemberId === m.id}
                              style={{
                                minHeight: '44px', padding: '0 14px', borderRadius: '8px', border: 'none', backgroundColor: AMBER, color: NAVY,
                                fontSize: '0.875rem', fontWeight: 700, fontFamily: FONT_BODY, whiteSpace: 'nowrap',
                                cursor: messagingMemberId === m.id ? 'default' : 'pointer', opacity: messagingMemberId === m.id ? 0.7 : 1,
                              }}
                            >
                              {t('organization.dashboard.members.messageButton')}
                            </button>
                          )}
                          {isOrgAdmin && !departed && (
                            <button
                              onClick={() => handleRemoveMember(m)}
                              disabled={!!memberActionSaving[m.id]}
                              style={{
                                background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.6)',
                                fontSize: '0.72rem', fontWeight: 700, padding: '5px 10px', borderRadius: '6px', fontFamily: FONT_BODY,
                                whiteSpace: 'nowrap',
                                cursor: memberActionSaving[m.id] ? 'default' : 'pointer',
                                opacity: memberActionSaving[m.id] ? 0.5 : 1,
                              }}
                            >
                              {t('organization.dashboard.members.removeButton')}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {memberActionError && (
                    <p style={{ fontSize: '0.8rem', color: RED, lineHeight: 1.5, backgroundColor: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.25)', borderRadius: '8px', padding: '10px 12px', margin: 0 }}>
                      {memberActionError}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Check-ins tab: coaching/support video calls with connected members, ── */}
          {/* separate from job interviews (see CheckInPanel / organization_checkins). */}
          {activeTab === 'Check-ins' && (
            <CheckInPanel
              viewerType="organization"
              viewerId={user?.id}
              organizationId={staffRow?.organization_id}
              counterparts={membersList.filter(m => !m.left_at).map(m => ({ id: m.worker_id, name: m.name }))}
              isMobile={isMobile}
            />
          )}

          {/* ── Invites tab (Phase 3 functionality, relocated) ── */}
          {activeTab === 'Invites' && (
            <div>
              <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '16px' }}>
                {t('organization.dashboard.invites.heading')}
              </p>

              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '1rem', marginBottom: '1.75rem' }}>
                {/* Staff invites are admin-only: a non-admin staff member can still invite
                    members, but adding another staff seat is an admin decision. */}
                {isOrgAdmin && (
                <InviteForm
                  inviteType="staff"
                  title={t('organization.dashboard.invites.inviteStaffHeading')}
                  form={staffForm}
                  setForm={setStaffForm}
                  submitting={staffSubmitting}
                  msg={staffMsg}
                  msgIsSuccess={staffMsgIsSuccess}
                  onSubmit={handleCreateInvite}
                />
                )}
                <InviteForm
                  inviteType="member"
                  title={t('organization.dashboard.invites.inviteMemberHeading')}
                  form={memberForm}
                  setForm={setMemberForm}
                  submitting={memberSubmitting}
                  msg={memberMsg}
                  msgIsSuccess={memberMsgIsSuccess}
                  onSubmit={handleCreateInvite}
                />
              </div>

              <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px' }}>
                {t('organization.dashboard.invites.sentInvitesHeading')}
              </p>

              {!invitesLoaded ? (
                <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.4)' }}>{t('organization.dashboard.shared.loading')}</p>
              ) : invites.length === 0 ? (
                <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.4)' }}>{t('organization.dashboard.invites.emptyNone')}</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {invites.map(inv => {
                    const badge = inviteStatusBadge(inv.status, t);
                    const canManage = inv.invite_type === 'member' && (inv.status || 'pending') === 'pending';
                    const feedback = inviteAction[inv.id];
                    const actionBtn = {
                      minHeight: '44px', padding: '0 14px', borderRadius: '8px', fontSize: '0.875rem', fontWeight: 700,
                      fontFamily: FONT_BODY, cursor: 'pointer', background: 'transparent', color: WHITE,
                      border: '1px solid rgba(255,255,255,0.25)', flex: isMobile ? '1 1 auto' : '0 0 auto',
                    };
                    return (
                      <div key={inv.id} data-invite-row={inv.id} style={{ backgroundColor: NAVY_MID, borderRadius: '8px', padding: '12px 14px' }}>
                      <div
                        style={{
                          display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center',
                          justifyContent: 'space-between', gap: isMobile ? '6px' : '10px',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '0.85rem', color: WHITE, fontWeight: 600 }}>{inv.email || '—'}</span>
                          <span style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.5)', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '999px', padding: '2px 10px' }}>
                            {typeLabel(inv.invite_type, t)}
                          </span>
                          <span style={{ fontSize: '0.68rem', fontWeight: 700, color: badge.color, backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: '999px', padding: '2px 10px' }}>
                            {badge.label}
                          </span>
                        </div>
                        <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.38)', flexShrink: 0 }}>
                          {new Date(inv.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      {canManage && (
                        confirmRevokeId === inv.id ? (
                          <div data-revoke-confirm style={{ marginTop: '10px', padding: '10px 12px', borderRadius: '8px', background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.35)' }}>
                            <p style={{ fontSize: '0.875rem', color: WHITE, marginBottom: '8px', lineHeight: 1.5 }}>{t('organization.dashboard.invites.revokeConfirm', { email: inv.email || '—' })}</p>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              <button type="button" onClick={() => handleRevokeInvite(inv)} disabled={inviteBusyId === inv.id} style={{ ...actionBtn, backgroundColor: '#DC2626', border: 'none' }}>
                                {t('organization.dashboard.invites.revokeYes')}
                              </button>
                              <button type="button" onClick={() => setConfirmRevokeId(null)} style={actionBtn}>{t('common.cancel')}</button>
                            </div>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}>
                            <button type="button" data-invite-copy onClick={() => handleCopyInviteLink(inv)} style={actionBtn}>
                              {feedback === 'copied' ? t('organization.dashboard.invites.copied') : t('organization.dashboard.invites.copyLink')}
                            </button>
                            <button type="button" data-invite-resend onClick={() => handleResendInvite(inv)} disabled={inviteBusyId === inv.id || !inv.email} style={{ ...actionBtn, opacity: !inv.email ? 0.5 : 1 }}>
                              {feedback === 'resent' ? t('organization.dashboard.invites.resent') : t('organization.dashboard.invites.resend')}
                            </button>
                            <button type="button" data-invite-revoke onClick={() => setConfirmRevokeId(inv.id)} style={{ ...actionBtn, color: '#F87171', borderColor: 'rgba(248,113,113,0.5)' }}>
                              {t('organization.dashboard.invites.revoke')}
                            </button>
                          </div>
                        )
                      )}
                      {typeof feedback === 'string' && feedback.startsWith('error:') && (
                        <p role="alert" style={{ fontSize: '0.875rem', color: '#F87171', marginTop: '8px' }}>{feedback.slice(6)}</p>
                      )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Messages tab ── */}
          {activeTab === 'Messages' && (
            <div>
              <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '16px' }}>
                {t('organization.dashboard.messages.heading')}
              </p>
              <div style={{ backgroundColor: NAVY_MID, borderRadius: '12px', overflow: 'hidden' }}>
                <SupportInbox
                  supabase={supabase}
                  accountType="organization"
                  accountId={staffRow?.organization_id}
                  viewerId={user?.id}
                />
              </div>
            </div>
          )}

          {/* ── Profile tab ── */}
          {activeTab === 'Profile' && profileForm && (
            <div style={{ maxWidth: 520 }}>
              <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '16px' }}>
                {t('organization.dashboard.profile.heading')}
              </p>

              {/* organizations UPDATE is admin-only at the database level now, so a non-admin
                  staff member is told up front instead of filling in a form whose save would
                  be rejected. Fields below render read-only for them. */}
              {!isOrgAdmin && (
                <p style={{
                  fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.5,
                  backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: '8px', padding: '12px 14px', marginBottom: '18px',
                }}>
                  {t('organization.dashboard.profile.adminOnlyNotice')}
                </p>
              )}

              {/* Logo upload */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', marginBottom: '1.75rem', paddingBottom: '1.5rem', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                <OrgAvatar logoUrl={org?.logo_url} initials={orgInitials} size={80} bg={NAVY} textColor={WHITE} />
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  style={{ display: 'none' }}
                  onChange={handleLogoUpload}
                />
                {isOrgAdmin && (
                <button
                  onClick={() => { setLogoMsg(''); logoInputRef.current?.click(); }}
                  disabled={logoUploading}
                  style={{ background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.82rem', padding: '7px 18px', borderRadius: '8px', cursor: logoUploading ? 'default' : 'pointer', fontFamily: FONT_BODY, opacity: logoUploading ? 0.7 : 1 }}
                >
                  {logoUploading ? t('organization.dashboard.profile.uploadingButton') : t('organization.dashboard.profile.uploadLogoButton')}
                </button>
                )}
                {logoMsg && (
                  <span style={{ fontSize: '0.82rem', fontWeight: 600, color: logoMsgIsError ? RED : GREEN }}>
                    {logoMsg}
                  </span>
                )}
              </div>

              <div style={fld}>
                <label style={lbl}>{t('organization.dashboard.profile.orgNameLabel')}</label>
                <input style={inp} value={profileForm.orgName} onChange={e => setPf('orgName', e.target.value)} disabled={!isOrgAdmin} />
              </div>

              <div style={g2}>
                <div>
                  <label style={lbl}>{t('organization.dashboard.profile.cityLabel')}</label>
                  <input style={inp} value={profileForm.city} onChange={e => setPf('city', e.target.value)} disabled={!isOrgAdmin} />
                </div>
                <div>
                  <label style={lbl}>{t('organization.dashboard.profile.stateLabel')}</label>
                  <select style={sel} value={profileForm.state} onChange={e => setPf('state', e.target.value)} disabled={!isOrgAdmin}>
                    <option value="IL">{t('organization.dashboard.profile.stateIL')}</option>
                    <option value="IN">{t('organization.dashboard.profile.stateIN')}</option>
                    <option value="WI">{t('organization.dashboard.profile.stateWI')}</option>
                    <option value="MI">{t('organization.dashboard.profile.stateMI')}</option>
                    <option value="OH">{t('organization.dashboard.profile.stateOH')}</option>
                    <option value="MO">{t('organization.dashboard.profile.stateMO')}</option>
                    <option value="IA">{t('organization.dashboard.profile.stateIA')}</option>
                    <option value="MN">{t('organization.dashboard.profile.stateMN')}</option>
                  </select>
                </div>
              </div>

              <div style={fld}>
                <label style={lbl}>{t('organization.dashboard.profile.aboutLabel')}</label>
                <textarea
                  style={{ ...inp, resize: 'vertical', minHeight: '90px', lineHeight: 1.5 }}
                  placeholder={t('organization.dashboard.profile.aboutPlaceholder')}
                  value={profileForm.about}
                  onChange={e => setPf('about', e.target.value)}
                  disabled={!isOrgAdmin}
                />
              </div>

              <div style={fld}>
                <label style={lbl}>{t('organization.dashboard.profile.websiteLabel')}</label>
                <input style={inp} placeholder={t('organization.dashboard.profile.websitePlaceholder')} value={profileForm.website} onChange={e => setPf('website', e.target.value)} disabled={!isOrgAdmin} />
              </div>

              {isOrgAdmin && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginTop: '8px' }}>
                <button
                  onClick={handleSaveProfile}
                  style={{ background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.88rem', padding: '10px 22px', borderRadius: '8px', cursor: 'pointer', fontFamily: FONT_BODY }}
                >
                  {t('organization.dashboard.profile.saveChangesButton')}
                </button>
                {profileMsg && (
                  <span style={{ fontSize: '0.84rem', fontWeight: 600, color: profileMsgIsError ? RED : GREEN }}>
                    {profileMsg}
                  </span>
                )}
              </div>
              )}
            </div>
          )}

          {/* ── Settings tab ── */}
          {activeTab === 'Settings' && (
            <div style={{ maxWidth: 480 }}>
              <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '16px' }}>
                {t('organization.dashboard.settings.heading')}
              </p>

              {/* ── My Profile: this individual's own name + photo, distinct from the org
                  logo/name every staff seat shares. Members see this on messages and check-ins,
                  so they know which person they're talking to. Staff may edit their own
                  display_name/avatar_url regardless of role -- only is_active/role are gated. */}
              <div style={{ background: NAVY_MID, borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
                <h3 style={{ fontFamily: FONT_BODY, fontSize: '0.95rem', fontWeight: 700, color: WHITE, marginBottom: '14px' }}>
                  {t('organization.dashboard.myProfile.heading')}
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', marginBottom: '18px' }}>
                  <OrgAvatar
                    logoUrl={staffRow?.avatar_url}
                    initials={((staffRow?.display_name || user?.user_metadata?.full_name || user?.email || '?')[0] || '?').toUpperCase()}
                    size={72} bg={NAVY} textColor={WHITE}
                  />
                  <input
                    id="org-staff-avatar-input"
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    style={{ display: 'none' }}
                    onChange={handleAvatarPick}
                  />
                  <button
                    onClick={() => { setMyProfileMsg(''); document.getElementById('org-staff-avatar-input')?.click(); }}
                    disabled={avatarUploading}
                    style={{ background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.82rem', padding: '7px 18px', borderRadius: '8px', cursor: avatarUploading ? 'default' : 'pointer', fontFamily: FONT_BODY, opacity: avatarUploading ? 0.7 : 1 }}
                  >
                    {avatarUploading ? t('organization.dashboard.profile.uploadingButton') : t('organization.dashboard.myProfile.uploadPhotoButton')}
                  </button>
                </div>
                <div style={fld}>
                  <label style={lbl}>{t('organization.dashboard.myProfile.displayNameLabel')}</label>
                  <input
                    style={inp}
                    placeholder={t('organization.dashboard.myProfile.displayNamePlaceholder')}
                    value={myNameForm}
                    onChange={e => setMyNameForm(e.target.value)}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <button
                    onClick={handleSaveMyName}
                    disabled={myProfileSaving}
                    style={{ background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.88rem', padding: '9px 20px', borderRadius: '8px', cursor: myProfileSaving ? 'default' : 'pointer', fontFamily: FONT_BODY, opacity: myProfileSaving ? 0.7 : 1 }}
                  >
                    {t('organization.dashboard.myProfile.saveButton')}
                  </button>
                  {myProfileMsg && (
                    <span style={{ fontSize: '0.84rem', fontWeight: 600, color: myProfileMsgIsError ? RED : GREEN }}>
                      {myProfileMsg}
                    </span>
                  )}
                </div>
              </div>

              {/* Change Password */}
              <div style={{ background: NAVY_MID, borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
                <h3 style={{ fontFamily: FONT_BODY, fontSize: '0.95rem', fontWeight: 700, color: WHITE, marginBottom: '14px' }}>{t('employer.dashboard.settings.changePasswordHeading')}</h3>
                <div style={fld}>
                  <label style={lbl}>{t('employer.dashboard.settings.newPasswordLabel')}</label>
                  <input style={inp} type="password" placeholder={t('worker.signup.step1.passwordPlaceholder')} value={pwForm.newPw} onChange={e => setPwForm(p => ({ ...p, newPw: e.target.value }))} />
                </div>
                <div style={fld}>
                  <label style={lbl}>{t('employer.dashboard.settings.confirmNewPasswordLabel')}</label>
                  <input style={inp} type="password" placeholder={t('worker.signup.step1.confirmPasswordPlaceholder')} value={pwForm.confirmPw} onChange={e => setPwForm(p => ({ ...p, confirmPw: e.target.value }))} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <button
                    onClick={handleChangePassword}
                    style={{ background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.88rem', padding: '9px 20px', borderRadius: '8px', cursor: 'pointer', fontFamily: FONT_BODY }}
                  >
                    {t('employer.dashboard.settings.updatePasswordButton')}
                  </button>
                  {pwMsg && (
                    <span style={{ fontSize: '0.84rem', fontWeight: 600, color: pwMsgIsSuccess ? GREEN : RED }}>
                      {pwMsg}
                    </span>
                  )}
                </div>
              </div>

              {/* Sign Out */}
              <div style={{ background: NAVY_MID, borderRadius: '12px', padding: '20px' }}>
                <h3 style={{ fontFamily: FONT_BODY, fontSize: '0.95rem', fontWeight: 700, color: WHITE, marginBottom: '6px' }}>{t('common.signOut')}</h3>
                <p style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.38)', marginBottom: '14px' }}>{t('worker.settings.signOutNote')}</p>
                <button
                  onClick={handleSignOut}
                  style={{
                    background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.35)',
                    color: '#F87171', fontFamily: FONT_BODY, fontSize: '0.88rem', fontWeight: 700,
                    padding: '9px 22px', borderRadius: '8px', cursor: 'pointer',
                  }}
                >
                  {t('common.signOut')}
                </button>
              </div>
            </div>
          )}
        </div>

        {isMobile && (
          <div style={{ padding: '20px 16px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <LegalLinksRow fontSize="0.7rem" />
            <p style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.25)', fontFamily: FONT_BODY }}>{t('common.copyright')}</p>
          </div>
        )}
      </main>

      {cropFile && (
        <ImageCropModal
          file={cropFile}
          onCancel={() => setCropFile(null)}
          onConfirm={handleLogoCropConfirm}
        />
      )}

      {avatarCropFile && (
        <ImageCropModal
          file={avatarCropFile}
          onCancel={() => setAvatarCropFile(null)}
          onConfirm={handleAvatarCropConfirm}
        />
      )}

      {/* ── Mobile Bottom Nav ── */}
      <nav className="org-bottom-nav" style={{
        display: 'none', position: 'fixed', bottom: 0, left: 0, right: 0,
        height: 'calc(58px + env(safe-area-inset-bottom))', paddingBottom: 'env(safe-area-inset-bottom)', boxSizing: 'border-box',
        backgroundColor: NAVY_MID, borderTop: '1px solid rgba(255,255,255,0.08)',
        zIndex: 200, alignItems: 'center', justifyContent: 'space-around',
      }}>
        {[
          { tab: 'Dashboard',  labelKey: 'organization.mobileNav.dashboard', Icon: IconGrid },
          { tab: 'Members',    labelKey: 'organization.mobileNav.members',   Icon: IconUsers },
          { tab: 'Check-ins',  labelKey: 'organization.mobileNav.checkins',  Icon: IconCheck },
          { tab: 'Invites',    labelKey: 'organization.mobileNav.invites',   Icon: IconMail },
          { tab: 'Messages',  labelKey: 'organization.mobileNav.messages',  Icon: IconMsg },
          { tab: 'Profile',   labelKey: 'organization.mobileNav.profile',   Icon: IconBuilding },
          { tab: 'Settings',  labelKey: 'organization.mobileNav.settings',  Icon: IconSettings },
        ].map(({ tab, labelKey, Icon }) => {
          const isActive = activeTab === tab;
          return (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              aria-current={isActive ? 'page' : undefined}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', padding: '4px 6px', cursor: 'pointer', flex: 1, background: 'none', border: 'none', margin: 0, font: 'inherit' }}
            >
              <Icon color={isActive ? AMBER : 'rgba(255,255,255,0.38)'} />
              <span style={{ fontSize: '0.56rem', fontWeight: 600, color: isActive ? AMBER : 'rgba(255,255,255,0.38)', textAlign: 'center' }}>
                {t(labelKey)}
              </span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
